import { useState, useCallback, useRef } from 'react';
import supabase from '../supabase/supabase';

export const useChat = () => {
  const [messages, setMessages] = useState([
    { id: 'init', role: 'ai', content: 'Quickly find the relevant videos. Try entering the kind of video you want, or maybe just your website.' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).substring(2, 15));

  // State for the email collection gate
  const [emailGated, setEmailGated] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const isFirstInteractionRef = useRef(true);

  const sendMessage = useCallback(async (content) => {
    if (!content.trim()) return;

    const userMsg = { id: Date.now().toString(), role: 'user', content };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      // 1. Send chat message via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('chat', {
        body: { sessionId, message: content }
      });

      if (error) {
        throw new Error(`Edge function error: ${error.message}`);
      }

      let aiContent = '';
      if (typeof data === 'string') {
        aiContent = data;
      } else if (Array.isArray(data) && data.length > 0) {
        aiContent = data[0].output || data[0].text || data[0].message || JSON.stringify(data[0]);
      } else if (data) {
        aiContent = data.output || data.text || data.message || data.response || JSON.stringify(data);
      }

      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'ai', content: aiContent }]);

      // 2. Trigger lead webhook after the first AI reply
      if (isFirstInteractionRef.current) {
        isFirstInteractionRef.current = false;
        setEmailGated(true);

        // Fire asynchronously via Lead Edge Function
        supabase.functions.invoke('lead', {
          body: {
            sessionId,
            query: content,
            response: aiContent,
            datetime: new Date().toISOString(),
            event: 'first_reply_sent'
          }
        }).catch(err => console.error("Lead webhook error:", err));
      }

    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: 'I encountered an error connecting to the server. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  const submitEmail = useCallback(async (email) => {
    setEmailGated(false);
    setEmailSubmitted(true);

    try {
      // Send email collection via Lead Edge Function
      await supabase.functions.invoke('lead', {
        body: {
          sessionId,
          email: email,
          datetime: new Date().toISOString(),
          event: 'email_provided'
        }
      });
    } catch (err) {
      console.error("Lead webhook email error:", err);
    }
  }, [sessionId]);

  return { messages, isLoading, sendMessage, emailGated, submitEmail, emailSubmitted };
};